import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
} from '@one-thd/sui-atomic-components';

/**
 * A button inside the draggable drawer that detects relevant pointer events.
 * @param { TriggerProps } props - The properties for the HandleBar component.
 * - `drawerState`: The state of the mobile drawer.
 * - `handlePointerDown`: Function to handle touch start.
 * - `handlePointerMove`: Function to handle touch move.
 * - `handlePointerUp`: Function to handle touch up.
 * @param { React.ForwardedRef<HTMLButtonElement> } ref - Reference to the drawer element.
 *
 * @returns {JSX.Element} The HandleBar button element.
 */
export const HandleBar = React.forwardRef(({
  handlePointerDown,
  handlePointerMove,
  handlePointerUp,
  drawerState
}, ref) => {
  const label = drawerState === 'OPEN' ? 'Drag down to minimize' : 'Drag to open or close';

  // eslint-disable-next-line max-len
  const handleBarStyles = 'sui-w-[15%] sui-h-[6px] sui-bg-strong sui-rounded-full sui-mt-2 sui-mb-2 sui-block sui-mx-auto';

  // TODO: Consider adding analytics events for pointer events to track user intentions.

  return (
    <Button
      className={handleBarStyles}
      onTouchStart={handlePointerDown}
      onTouchMove={handlePointerMove}
      onTouchEnd={handlePointerUp}
      onMouseDown={handlePointerDown}
      onMouseMove={handlePointerMove}
      onMouseUp={handlePointerUp}
      ref={ref}
    >
      <span className="sui-sr-only">{label}</span>
    </Button>
  );
});

HandleBar.displayName = 'HandleBar';

HandleBar.propTypes = {
  drawerState: PropTypes.oneOf(['OPEN', 'MINIMIZED', 'CLOSED']).isRequired,
  handlePointerDown: PropTypes.func.isRequired,
  handlePointerMove: PropTypes.func.isRequired,
  handlePointerUp: PropTypes.func.isRequired,
};